import * as React from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import parse from "autosuggest-highlight/parse";
import throttle from "lodash/throttle";
import {Controller, useController} from "react-hook-form";
import {useEffect} from "react";

function loadScript(src, position, id) {
    if (!position) {
        return;
    }

    const script = document.createElement("script");
    script.setAttribute("async", "");
    script.setAttribute("id", id);
    script.src = src;
    position.appendChild(script);
}

const autocompleteService = {current: null};
const geocoderService = {current: null};
const googlePlacesKey = process.env.REACT_APP_GOOGLE_PLACES_API;

export default function GoogleMaps({callback, resetCallback, control, name, label, errors, inputPrp, onValuChange, valueOnLoad, floridaOnly}) {
    const [value, setValue] = React.useState(null);
    const [inputValue, setInputValue] = React.useState("");
    const [options, setOptions] = React.useState([]);
    const loaded = React.useRef(false);

    useEffect(() => {
        if (valueOnLoad && valueOnLoad !== '') {
            setInputValue(valueOnLoad);
            setValue(valueOnLoad);
        }
    },[]);

    if (typeof window !== "undefined" && !loaded.current) {
        if (!document.querySelector("#google-maps")) {
            loadScript(
                "https://maps.googleapis.com/maps/api/js?key=" + googlePlacesKey + "&libraries=places,geocoder",
                document.querySelector("head"),
                "google-maps"
            );
        }
        loaded.current = true;
    }
    // You can only restrict to country and not to a state.
    // Even though we don't specify it automatically detect user location from IP address and results will be based on that location
    // Address sets only to property addresses and eliminates business matches
    // https://developers.google.com/maps/documentation/places/web-service/supported_types#table3
    const placesOptions = {componentRestrictions: {country: ["US", "pr", "vi","gu","mp", "as"]}, types: ["address"]}

    const fetch = React.useMemo(
        () =>
            throttle((request, callback) => {
                request = {...request, ...placesOptions}
                autocompleteService.current.getPlacePredictions(request, callback);
            }, 200),
        []
    );

    React.useEffect(() => {
        let active = true;

        if (!autocompleteService.current && window.google) {
            autocompleteService.current = new window.google.maps.places.AutocompleteService();
        }
        if (!geocoderService.current && window.google) {
            geocoderService.current = new window.google.maps.Geocoder();
        }
        if (!autocompleteService.current) {
            return undefined;
        }

        if (inputValue === "") {
            setOptions(value ? [value] : []);
            //resetCallback()
            return undefined;
        }

        fetch({input: inputValue}, (results) => {
            if (active) {
                let newOptions = [];

                if (value) {
                    newOptions = [value];
                }
                if (!!floridaOnly && results && results.length > 0) {
                    let floridaResults = results.filter(addr => addr.structured_formatting.secondary_text.includes('FL') || addr.structured_formatting.secondary_text.includes('Fl'));
                    newOptions = [...newOptions, ...floridaResults];
                } else if (results && results.length > 0) {
                    newOptions = [...newOptions, ...results];
                }
                setOptions(newOptions);
            }
        });
        if (value) {
            geocoderService.current.geocode({placeId: value.place_id}, (data) => callback(data))
            //console.log('user selected a value', value)
        }
        return () => {
            active = false;
        };
    }, [value, inputValue, fetch]);

    let err = (errors && errors[name]) ? errors[name].message : false;
    return (
        <>
            <Controller
                name={name}
                render={({field}) => (
                    <Autocomplete {...field}
                        freeSolo inputProps={inputPrp}
                        id="google-map-demo"
                        getOptionLabel={(option) =>
                            typeof option === "string" ? option : option.description
                        }
                        filterOptions={(x) => x}
                        options={options}
                        autoComplete
                        includeInputInList
                        filterSelectedOptions
                        value={value}

                        onChange={(event, newValue) => {
                            field.onChange(event);
                            setOptions(newValue ? [newValue, ...options] : options);
                            setValue(newValue);

                        }}
                        onInputChange={(event, newInputValue, reason) => {
                            field.onChange(event);
                            setInputValue(newInputValue);
                            onValuChange(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label={label} fullWidth name={name} style={{fontFamily:"cursive"}} />
                        )}
                        renderOption={(props, option) => {
                            const matches =
                                option?.structured_formatting?.main_text_matched_substrings;
                            const parts = parse(
                                option?.structured_formatting?.main_text,
                                matches.map((match) => [match.offset, match.offset + match.length])
                            );

                            return (
                                <li {...props}>
                                    <Grid container alignItems="center">
                                        <Grid item>
                                            <Box
                                                component={LocationOnIcon}
                                                sx={{color: "text.secondary", mr: 2}}
                                            />
                                        </Grid>
                                        <Grid item xs>
                                            {parts.map((part, index) => (
                                                <span
                                                    key={index}
                                                    style={{
                                                        fontWeight: part.highlight ? 700 : 400, fontFamily:"AvenirLTStd"
                                                    }}
                                                >
                    {part.text}
                  </span>
                                            ))}

                                            <Typography variant="body2" color="text.secondary" style={{fontFamily:"AvenirLTStd"}}>
                                                {option.structured_formatting.secondary_text}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </li>
                            );
                        }}
                    />)}
                control={control}
                defaultValue={value ?? options[0]}
            />
            {err && <span className='errorMessage'>{err}</span>}
        </>
    );
}
